import { Reveal, MaskLine } from "@/components/site/Reveal";
import ExportMap from "@/components/site/ExportMap";
import CTABand from "@/components/site/CTABand";
import { EXPORT_FACTS, RAW_MATERIALS, MACHINERY, PORT_REGIONS } from "@/data/content";
import { Ship, Handshake } from "lucide-react";

const IndustryPage = () => (
  <main data-testid="page-industry" className="bg-brand-choc text-brand-cream">
    {/* HERO */}
    <section className="relative overflow-hidden pt-36 pb-24 md:pt-48 md:pb-28">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <MaskLine><p className="eyebrow-gold">Biscuit industry · Global</p></MaskLine>
        <h1 className="mt-6 font-display font-light tracking-tight leading-[0.98] text-5xl sm:text-6xl lg:text-8xl">
          <MaskLine delay={0.1}>The world eats biscuits.</MaskLine>
          <MaskLine delay={0.3}><span className="italic text-brand-gold">We bake for it.</span></MaskLine>
        </h1>
        <MaskLine delay={0.5}>
          <p className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-brand-cream/80">
            Biscuits are the world's most shared packaged food — and the Gulf and African markets
            are among its hungriest. Since 1998, National has baked for exactly these shelves.
          </p>
        </MaskLine>
      </div>
    </section>

    {/* FACT STRIP */}
    <section className="border-y border-brand-cream/10">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6" data-testid="export-facts">
        {EXPORT_FACTS.map((f, i) => (
          <Reveal key={f.k} delay={i * 0.06} className="border-r border-brand-cream/10 px-5 py-8 last:border-r-0">
            <p className="font-mono text-2xl md:text-3xl text-brand-gold">{f.k}</p>
            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-brand-cream/60">{f.v}</p>
          </Reveal>
        ))}
      </div>
    </section>

    {/* EXPORT MODELS */}
    <section className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 py-24 md:py-32">
      <div className="grid items-center gap-12 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <Reveal><p className="eyebrow-gold">Two ways to buy from us</p></Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-5 font-display font-light text-4xl md:text-6xl tracking-tight">Own direct export <span className="italic text-brand-gold">&</span> merchant exports.</h2>
          </Reveal>
          <div className="mt-10 grid gap-6 sm:grid-cols-2">
            <Reveal delay={0.12}>
              <div className="h-full rounded-2xl border border-brand-cream/15 bg-brand-cream/5 p-7">
                <Ship className="h-7 w-7 text-brand-gold" />
                <h3 className="mt-4 font-display text-2xl">Own direct export</h3>
                <p className="mt-3 text-sm leading-relaxed text-brand-cream/70">Importers and distributors buy straight from our factory in India — our team handles production, documentation and stuffing to your port.</p>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="h-full rounded-2xl border border-brand-cream/15 bg-brand-cream/5 p-7">
                <Handshake className="h-7 w-7 text-brand-gold" />
                <h3 className="mt-4 font-display text-2xl">Merchant exports</h3>
                <p className="mt-3 text-sm leading-relaxed text-brand-cream/70">Through merchant export partners, our biscuits reach markets and channels we don't serve directly — same factory, same quality, same crest.</p>
              </div>
            </Reveal>
          </div>
        </div>
        <Reveal delay={0.18} className="lg:col-span-5">
          <img src="images/ship_export.jpeg" alt="Container ship carrying National exports" className="aspect-[4/5] w-full rounded-2xl object-cover" />
        </Reveal>
      </div>
    </section>

    {/* MAP */}
    <section className="border-t border-brand-cream/10 bg-brand-chocsoft/60">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 py-24 md:py-32">
        <Reveal>
          <p className="eyebrow-gold">Where we ship</p>
          <h2 className="mt-5 max-w-3xl font-display font-light text-4xl md:text-6xl leading-[1.05] tracking-tight">
            Concentrated on <span className="italic text-brand-gold">Africa &amp; the Gulf.</span>
          </h2>
        </Reveal>
        <div className="mt-14">
          <ExportMap />
        </div>
      </div>
    </section>

    {/* RAW MATERIALS */}
    <section className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 py-24 md:py-32">
      <Reveal>
        <p className="eyebrow-gold">What goes in</p>
        <h2 className="mt-5 font-display font-light text-4xl md:text-6xl tracking-tight">Only the finest raw materials.</h2>
      </Reveal>
      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="industry-materials">
        {RAW_MATERIALS.map((r, i) => (
          <Reveal key={r.name} delay={i * 0.07}>
            <div className="group overflow-hidden rounded-2xl border border-brand-cream/15 bg-brand-cream/5">
              <div className="h-44 overflow-hidden">
                <img src={r.img} alt={r.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="p-6">
                <h3 className="font-display text-xl">{r.name}</h3>
                <p className="mt-2 text-sm leading-relaxed text-brand-cream/70">{r.body}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    {/* MACHINERY SUMMARY */}
    <section className="border-t border-brand-cream/10">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 py-20 md:py-28">
        <Reveal><p className="eyebrow-gold">How it's made</p></Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-5 font-display font-light text-4xl md:text-5xl tracking-tight">A state-of-the-art line, end to end.</h2>
        </Reveal>
        <div className="mt-10 flex flex-wrap gap-3" data-testid="machinery-chips">
          {MACHINERY.map((m) => (
            <span key={m.no} className="rounded-full border border-brand-cream/20 px-5 py-2.5 text-sm text-brand-cream/85">{m.name}</span>
          ))}
        </div>
      </div>
    </section>

    <CTABand />
  </main>
);

export default IndustryPage;
